const API_CACHE_PREFIX = 'ysm:api-cache:'
const API_CACHE_INDEX_KEY = `${API_CACHE_PREFIX}index`

/** 普通 GET 默认缓存 20s */
export const DEFAULT_API_CACHE_TTL = 20 * 1000
/** 配置/公开列表：短缓存，配合后台刷新 */
export const STATIC_API_CACHE_TTL = 30 * 1000
/** 账户/余额/订单：更短 */
export const VOLATILE_API_CACHE_TTL = 10 * 1000

/** 配置类 / 公开列表 */
export const STATIC_CACHE_PATTERNS = [
  /\/api\/v1\/webViewConfig\/?$/,
  /\/api\/v1\/lottery\/prizes\/?$/,
  /\/api\/v1\/mall\/products\/?$/,
]

/** 账户 / 余额 / 订单 / 公告 / 状态：短缓存 */
export const VOLATILE_CACHE_PATTERNS = [
  /\/api\/v1\/notification\/?$/,
  /\/api\/v1\/user\/usersProfile\/?$/,
  /\/api\/v1\/user\/check-in\/?$/,
  /\/api\/v1\/user\/fundFlowList\/?$/,
  /\/api\/v1\/user\/bankCard\/?$/,
  /\/api\/v1\/user\/withdraw\/?$/,
  /\/api\/v1\/user\/addresses\/?$/,
  /\/api\/v1\/user\/referral\/rewards\/?$/,
  /\/api\/v1\/user\/invite\/info\/?$/,
  /\/api\/v1\/user\/payment-password\/?$/,
  /\/api\/v1\/user\/asset-market\/?$/,
  /\/api\/v1\/user\/detail\/[^/]+\/getPromotions\/?$/,
  /\/api\/v1\/home-personnel\/?$/,
  /\/api\/v1\/mall\/orders\/?$/,
  /\/api\/v1\/lottery\/records\/?$/,
]

/** 有副作用或不该缓存的 GET */
export const NO_CACHE_PATTERNS = [
  /\/api\/v1\/app-download-log\/?$/,
]

const pendingRevalidates = new Set()

function getCacheStorage() {
  try {
    return sessionStorage
  } catch (e) {
    return null
  }
}

function stableStringify(value) {
  if (value == null) return ''
  if (typeof value !== 'object') return String(value)
  if (Array.isArray(value)) {
    return `[${value.map((item) => stableStringify(item)).join(',')}]`
  }
  const keys = Object.keys(value).sort()
  return `{${keys.map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`).join(',')}}`
}

function hashString(input) {
  const str = String(input || '')
  let hash = 0
  for (let i = 0; i < str.length; i += 1) {
    hash = (hash << 5) - hash + str.charCodeAt(i)
    hash |= 0
  }
  return `h${Math.abs(hash)}`
}

function readIndex(storage) {
  try {
    const raw = storage.getItem(API_CACHE_INDEX_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch (e) {
    return []
  }
}

function writeIndex(storage, keys) {
  try {
    storage.setItem(API_CACHE_INDEX_KEY, JSON.stringify(keys))
  } catch (e) {
    // ignore quota errors
  }
}

function trackCacheKey(storage, cacheKey) {
  const index = readIndex(storage)
  if (!index.includes(cacheKey)) {
    index.push(cacheKey)
    writeIndex(storage, index)
  }
}

function untrackCacheKey(storage, cacheKey) {
  const index = readIndex(storage).filter((key) => key !== cacheKey)
  writeIndex(storage, index)
}

export function getRequestUrl(config) {
  const raw = config.url || ''
  try {
    if (/^https?:\/\//i.test(raw)) {
      return new URL(raw).pathname
    }
    const base = config.baseURL || ''
    if (base && /^https?:\/\//i.test(base)) {
      return new URL(raw, base).pathname
    }
  } catch (e) {
    // fall through
  }
  return raw.split('?')[0]
}

export function isCacheableRequest(config) {
  if (!config || config.skipApiCache || config.force) return false
  const method = (config.method || 'get').toLowerCase()
  if (method !== 'get') return false
  const url = getRequestUrl(config)
  return !NO_CACHE_PATTERNS.some((pattern) => pattern.test(url))
}

export function getCacheTTL(config) {
  if (typeof config.cacheTTL === 'number') return config.cacheTTL
  const url = getRequestUrl(config)
  if (STATIC_CACHE_PATTERNS.some((pattern) => pattern.test(url))) {
    return STATIC_API_CACHE_TTL
  }
  if (VOLATILE_CACHE_PATTERNS.some((pattern) => pattern.test(url))) {
    return VOLATILE_API_CACHE_TTL
  }
  return DEFAULT_API_CACHE_TTL
}

export function getCacheKey(config, token) {
  if (config.cacheKey) {
    return `${API_CACHE_PREFIX}${config.cacheKey}`
  }

  const baseURL = config.baseURL || process.env.VUE_APP_BASE_API || ''
  const url = getRequestUrl(config)
  const params = stableStringify(config.params || {})
  const scope = token ? `u:${hashString(token)}` : 'public'

  return `${API_CACHE_PREFIX}${scope}:${baseURL}:${url}:${params}`
}

export function readApiCache(cacheKey, allowStale = false) {
  const storage = getCacheStorage()
  if (!storage || !cacheKey) return null

  try {
    const raw = storage.getItem(cacheKey)
    if (raw == null) return null

    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object' || !('data' in parsed)) {
      return null
    }

    const expired = typeof parsed.expiresAt === 'number' && parsed.expiresAt <= Date.now()
    if (expired && !allowStale) return null
    return parsed.data
  } catch (e) {
    return null
  }
}

export function writeApiCache(cacheKey, data, ttl) {
  const storage = getCacheStorage()
  if (!storage || !cacheKey || typeof ttl !== 'number' || ttl <= 0) return

  const entry = {
    data,
    expiresAt: Date.now() + ttl,
  }

  try {
    storage.setItem(cacheKey, JSON.stringify(entry))
    trackCacheKey(storage, cacheKey)
  } catch (e) {
    try {
      const index = readIndex(storage)
      index.slice(0, Math.min(5, index.length)).forEach((key) => {
        storage.removeItem(key)
        untrackCacheKey(storage, key)
      })
      storage.setItem(cacheKey, JSON.stringify(entry))
      trackCacheKey(storage, cacheKey)
    } catch (err) {
      // ignore
    }
  }
}

export function clearApiCache() {
  const storage = getCacheStorage()
  if (!storage) return

  try {
    const index = readIndex(storage)
    index.forEach((key) => storage.removeItem(key))
    storage.removeItem(API_CACHE_INDEX_KEY)

    const keysToRemove = []
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i)
      if (key && key.startsWith(API_CACHE_PREFIX)) {
        keysToRemove.push(key)
      }
    }
    keysToRemove.forEach((key) => storage.removeItem(key))
  } catch (e) {
    // ignore
  }
}

/**
 * 命中缓存后后台静默拉新，写回缓存；下次进入即可看到新数据。
 * revalidateFn 需自行带 skipApiCache / force，避免再次命中缓存死循环。
 */
export function revalidateApiCache(cacheKey, revalidateFn, ttl) {
  if (!cacheKey || typeof revalidateFn !== 'function') return
  if (pendingRevalidates.has(cacheKey)) return

  pendingRevalidates.add(cacheKey)
  Promise.resolve()
    .then(() => revalidateFn())
    .then((data) => {
      if (data !== undefined && data !== null) {
        writeApiCache(cacheKey, data, ttl)
        try {
          window.dispatchEvent(
            new CustomEvent('api-cache:revalidated', {
              detail: { cacheKey, data },
            })
          )
        } catch (e) {
          // ignore
        }
      }
    })
    .catch(() => {})
    .finally(() => {
      pendingRevalidates.delete(cacheKey)
    })
}

/** 离开页面超过一段时间再回来时清空接口缓存，避免旧公告/配置卡住 */
export function bindApiCacheLifecycle() {
  if (typeof document === 'undefined') return
  if (bindApiCacheLifecycle.bound) return
  bindApiCacheLifecycle.bound = true

  let hiddenAt = 0
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      hiddenAt = Date.now()
      return
    }
    if (hiddenAt && Date.now() - hiddenAt > 15 * 1000) {
      clearApiCache()
    }
    hiddenAt = 0
  })
}
